import { Color, GameState, GRID, Particle, Piece } from './types';
import { generateTray } from './pieces';

export function createGrid(): (Color | null)[][] {
  return Array.from({ length: GRID }, () => Array<Color | null>(GRID).fill(null));
}

export function createInitialState(best = 0): GameState {
  return {
    grid: createGrid(),
    tray: generateTray(),
    score: 0,
    best,
    combo: 0,
    clearedInSet: false,
    phase: 'placing',
    clearAnim: null,
    particles: [],
  };
}

export function canPlace(grid: (Color | null)[][], piece: Piece, row: number, col: number): boolean {
  for (const { r, c } of piece.cells) {
    const gr = row + r;
    const gc = col + c;
    if (gr < 0 || gr >= GRID || gc < 0 || gc >= GRID) return false;
    if (grid[gr][gc] !== null) return false;
  }
  return true;
}

function findFullLines(grid: (Color | null)[][]): { rows: number[]; cols: number[] } {
  const rows: number[] = [];
  const cols: number[] = [];
  for (let r = 0; r < GRID; r++) {
    if (grid[r].every(cell => cell !== null)) rows.push(r);
  }
  for (let c = 0; c < GRID; c++) {
    if (grid.every(row => row[c] !== null)) cols.push(c);
  }
  return { rows, cols };
}

export function placePiece(
  state: GameState,
  trayIdx: number,
  row: number,
  col: number,
  nowMs: number,
): GameState {
  const piece = state.tray[trayIdx];
  if (!piece || !canPlace(state.grid, piece, row, col)) return state;

  const newGrid = state.grid.map(r => [...r]);
  for (const { r, c } of piece.cells) {
    newGrid[row + r][col + c] = piece.color;
  }

  const newTray = [...state.tray] as (Piece | null)[];
  newTray[trayIdx] = null;

  const placedScore = piece.cells.length;
  const { rows: fullRows, cols: fullCols } = findFullLines(newGrid);
  const lineCount = fullRows.length + fullCols.length;

  if (lineCount > 0) {
    return {
      ...state,
      grid: newGrid,
      tray: newTray,
      score: state.score + placedScore,
      clearedInSet: true,
      phase: 'clearing',
      clearAnim: { rows: fullRows, cols: fullCols, startMs: nowMs, durationMs: 350 },
    };
  }

  // No lines — check if set is done
  const allPlaced = newTray.every(p => p === null);
  if (allPlaced) {
    const combo = state.clearedInSet ? state.combo + 1 : 0;
    const nextTray = generateTray();
    const score = state.score + placedScore;
    const best  = Math.max(state.best, score);
    const gameOver = isGameOver(newGrid, nextTray as Piece[]);
    return {
      ...state,
      grid: newGrid,
      tray: nextTray,
      score,
      best,
      combo,
      clearedInSet: false,
      phase: gameOver ? 'game_over' : 'placing',
    };
  }

  // Check if remaining pieces can still be placed
  const remaining = newTray.filter(Boolean) as Piece[];
  if (isGameOver(newGrid, remaining)) {
    return {
      ...state,
      grid: newGrid,
      tray: newTray,
      score: state.score + placedScore,
      best: Math.max(state.best, state.score + placedScore),
      phase: 'game_over',
    };
  }

  return { ...state, grid: newGrid, tray: newTray, score: state.score + placedScore };
}

export function finishClear(state: GameState, particles: Particle[]): GameState {
  if (!state.clearAnim) return state;

  const { rows, cols } = state.clearAnim;
  const newGrid = state.grid.map(r => [...r]);
  for (const r of rows) for (let c = 0; c < GRID; c++) newGrid[r][c] = null;
  for (const c of cols) for (let r = 0; r < GRID; r++) newGrid[r][c] = null;

  const lineCount = rows.length + cols.length;
  const lineScore = lineCount * (lineCount + 1) / 2 * 10 * (state.combo + 1);
  const score     = state.score + lineScore;
  const best      = Math.max(state.best, score);

  const allPlaced = state.tray.every(p => p === null);
  let newTray     = state.tray as (typeof state.tray);
  let combo       = state.combo;
  let clearedInSet = state.clearedInSet;

  if (allPlaced) {
    combo        = state.clearedInSet ? state.combo + 1 : 0;
    newTray      = generateTray();
    clearedInSet = false;
  }

  const gameOver = isGameOver(newGrid, newTray.filter(Boolean) as Piece[]);
  return {
    ...state,
    grid: newGrid,
    tray: newTray,
    score,
    best,
    combo,
    clearedInSet,
    phase: gameOver ? 'game_over' : 'placing',
    clearAnim: null,
    particles,
  };
}

export function isGameOver(grid: (Color | null)[][], pieces: Piece[]): boolean {
  for (const piece of pieces) {
    for (let r = 0; r <= GRID - piece.rows; r++) {
      for (let c = 0; c <= GRID - piece.cols; c++) {
        if (canPlace(grid, piece, r, c)) return false;
      }
    }
  }
  return true;
}

export function updateParticles(particles: Particle[], dtMs: number): Particle[] {
  const dt = dtMs / 1000;
  return particles
    .map(p => ({
      ...p,
      x: p.x + p.vx * dt,
      y: p.y + p.vy * dt,
      vy: p.vy + 400 * dt, // gravity
      life: p.life - dt * 1.8,
    }))
    .filter(p => p.life > 0);
}
